/**
 * Imperative controller for MapLibre bike station sources, layers, and interactions.
 */

import type { FeatureCollection } from "geojson";
import type maplibregl from "maplibre-gl";

import {
    MAP_BIKE_STATIONS_MIN_PRELOAD_ZOOM,
    MAP_BIKE_STATIONS_PRELOAD,
} from "../../../config/global.configuration";
import { BboxPreloadStore } from "../bboxPreload";
import { mapBoundsToBbox } from "../bboxPreload/bboxPreload.tools";
import type { MapViewportContext } from "../bboxPreload/bboxPreload.types";
import {
    BIKE_STATIONS_BACKGROUND_LAYER_ID,
    BIKE_STATIONS_LAYER_ID,
    BIKE_STATIONS_SOURCE_ID,
} from "../mapLayerIds";
import {
    applyBikeStationLayerTheme,
    bikeStationsToFeatureCollection,
    EMPTY_BIKE_STATIONS_FEATURE_COLLECTION,
    ensureBikeStationLayers,
    ensureBikeStationSources,
    loadBikeStationMapIcon,
} from "./bikeStationMapLayers.helpers";
import type { BikeStationClickPayload, BikeStationMapLayersAttachOptions } from "./bikeStationMapLayers.types";
import { resolveBikeStationDisplayName } from "./bikeStationPopupModern.tools";
import { defaultFetchBikeStationsForBbox, handleBikeStationsFetchError } from "./bikeStationPreloadBbox.tools";

/**
 * Manages bike station GeoJSON sources, symbol layers, bbox preload fetching, and click handlers
 * on a MapLibre map instance.
 */
export class BikeStationMapLayersController {

    private map: maplibregl.Map | null = null;

    private onBikeStationClick: ( ( payload: BikeStationClickPayload ) => void ) | null = null;

    private isDarkUi = false;

    private isAttached = false;

    private isVisible = false;

    private readonly preloadStore = new BboxPreloadStore(
        MAP_BIKE_STATIONS_MIN_PRELOAD_ZOOM,
        MAP_BIKE_STATIONS_PRELOAD,
        defaultFetchBikeStationsForBbox,
        {
            "countItems":   ( data ) => data.length,
            "loggerName":   "BikeStationMapLayersController",
            "onFetchError": handleBikeStationsFetchError,
        },
    );

    private viewportDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    /** Bumped on detach and at attach start to ignore stale async attach work after style swaps. */
    private attachGeneration = 0;

    private readonly handleClick = ( event: maplibregl.MapMouseEvent & { "features"?: maplibregl.MapGeoJSONFeature[] } ): void => {
        const feature = event.features?.[0];
        if ( feature === undefined || this.onBikeStationClick === null ) {
            return;
        }

        const [ lng, lat ] = ( feature.geometry as GeoJSON.Point ).coordinates;
        const stationId    = String( feature.properties?.id ?? "" );
        const operator     = String( feature.properties?.operator ?? "" );

        const stations    = this.preloadStore.getPreloadedData();
        const station     = stations?.find( ( s ) => s.stationId === stationId && s.operator === operator );
        const rentalUris  = station?.rentalUris;

        this.onBikeStationClick( {
            "lngLat":         [ lng, lat ],
            "name":           resolveBikeStationDisplayName( feature.properties?.name ),
            "stationId":      stationId,
            "operator":       operator,
            "operatorName":   resolveBikeStationDisplayName( station?.operatorName ),
            "rentalUris":     rentalUris,
            "bikesAvailable": station?.bikesAvailable ?? null,
            "capacity":       station?.capacity ?? null,
            "x":              event.originalEvent.clientX,
            "y":              event.originalEvent.clientY,
        } );
    };

    private readonly handleMouseEnter = (): void => {
        this.map?.getContainer().classList.add( "map-pointer-cursor" );
    };

    private readonly handleMouseLeave = (): void => {
        this.map?.getContainer().classList.remove( "map-pointer-cursor" );
    };

    private readonly handleViewportSettled = (): void => {
        if ( !this.isVisible ) {
            return;
        }

        this.scheduleSyncFromPreload();
    };

    private readonly handleVisibilityChange = (): void => {
        if ( document.visibilityState !== "visible" ) {
            return;
        }

        if (
            !this.isAttached ||
            !this.isVisible ||
            !MAP_BIKE_STATIONS_PRELOAD.refreshOnPageVisible
        ) {
            return;
        }

        void this.syncFromPreload( { "force": true } );
    };

    /**
     * Attaches bike station layers and interaction handlers to the map.
     * Idempotent: detaches existing handlers before re-attaching.
     *
     * @param map Target MapLibre map instance.
     * @param options Click callback configuration.
     */
    async attach( map: maplibregl.Map, options: BikeStationMapLayersAttachOptions ): Promise<void> {
        this.detach();

        const attachId = ++this.attachGeneration;

        this.map                 = map;
        this.isDarkUi            = options.isDarkUi;
        this.onBikeStationClick  = options.onBikeStationClick;

        await loadBikeStationMapIcon( map, this.isDarkUi );

        if ( this.isAttachCancelled( attachId, map ) ) {
            return;
        }

        ensureBikeStationSources( map );
        ensureBikeStationLayers( map, this.isDarkUi );

        if ( this.isAttachCancelled( attachId, map ) ) {
            return;
        }

        map.on( "click",      BIKE_STATIONS_LAYER_ID, this.handleClick );
        map.on( "mouseenter", BIKE_STATIONS_LAYER_ID, this.handleMouseEnter );
        map.on( "mouseleave", BIKE_STATIONS_LAYER_ID, this.handleMouseLeave );
        map.on( "moveend",    this.handleViewportSettled );
        map.on( "zoomend",    this.handleViewportSettled );

        this.isAttached = true;
        document.addEventListener( "visibilitychange", this.handleVisibilityChange );
    }

    /**
     * Removes bike station interaction handlers from the current map.
     * Sources and layers remain on the map until the style is replaced.
     */
    detach(): void {
        this.attachGeneration += 1;
        this.clearViewportDebounce();

        if ( this.map !== null && this.isAttached ) {
            this.map.off( "click",      BIKE_STATIONS_LAYER_ID, this.handleClick );
            this.map.off( "mouseenter", BIKE_STATIONS_LAYER_ID, this.handleMouseEnter );
            this.map.off( "mouseleave", BIKE_STATIONS_LAYER_ID, this.handleMouseLeave );
            this.map.off( "moveend",    this.handleViewportSettled );
            this.map.off( "zoomend",    this.handleViewportSettled );
        }

        this.preloadStore.reset();
        this.isVisible = false;

        if ( typeof document !== "undefined" ) {
            document.removeEventListener( "visibilitychange", this.handleVisibilityChange );
        }

        this.isAttached = false;
    }

    /**
     * Toggles layer visibility and triggers a preload sync when becoming visible.
     *
     * @param visible Whether the bike station layers should be shown.
     */
    setVisibility( visible: boolean ): void {
        const map = this.map;
        if ( map === null || !this.isAttached ) {
            return;
        }

        const visibility = visible ? "visible" : "none";
        map.setLayoutProperty( BIKE_STATIONS_LAYER_ID, "visibility", visibility );
        map.setLayoutProperty( BIKE_STATIONS_BACKGROUND_LAYER_ID, "visibility", visibility );

        this.isVisible = visible;

        if ( visible ) {
            void this.syncFromPreload();
        }
    }

    /**
     * Updates layer colors and icon tint to match the current UI theme.
     *
     * @param isDarkUi Whether dark UI palette is active.
     * @returns Promise resolved once the theme is applied.
     */
    async applyTheme( isDarkUi: boolean ): Promise<void> {
        this.isDarkUi = isDarkUi;

        if ( this.map === null || !this.isAttached ) {
            return;
        }

        await applyBikeStationLayerTheme( this.map, isDarkUi );
    }

    /**
     * Whether an in-flight {@link attach} was superseded by detach or a newer attach.
     *
     * @param attachId Generation captured at attach start.
     * @param map Map instance passed to attach.
     * @returns True when this attach should abort.
     */
    private isAttachCancelled( attachId: number, map: maplibregl.Map ): boolean {
        return attachId !== this.attachGeneration || this.map !== map;
    }

    /**
     * Debounces preload sync after viewport move/zoom end.
     */
    private scheduleSyncFromPreload(): void {
        this.clearViewportDebounce();

        this.viewportDebounceTimer = setTimeout( () => {
            this.viewportDebounceTimer = null;
            void this.syncFromPreload();
        }, MAP_BIKE_STATIONS_PRELOAD.viewportDebounceMs );
    }

    /**
     * Clears the pending viewport debounce timer.
     */
    private clearViewportDebounce(): void {
        if ( this.viewportDebounceTimer !== null ) {
            clearTimeout( this.viewportDebounceTimer );
            this.viewportDebounceTimer = null;
        }
    }

    /**
     * Builds the viewport context snapshot for the preload store.
     *
     * @param map Active MapLibre map.
     * @returns Context or null when the container has no measurable size.
     */
    private buildViewportContext( map: maplibregl.Map ): MapViewportContext | null {
        const container = map.getContainer();
        const widthPx   = container.clientWidth;
        const heightPx  = container.clientHeight;

        if ( widthPx <= 0 || heightPx <= 0 ) {
            return null;
        }

        const center = map.getCenter();

        return {
            "visibleBbox":      mapBoundsToBbox( map.getBounds() ),
            "centerLat":        center.lat,
            "centerLng":        center.lng,
            "viewportWidthPx":  widthPx,
            "viewportHeightPx": heightPx,
            "currentZoom":      map.getZoom(),
        };
    }

    /**
     * Applies GeoJSON to the bike station source.
     *
     * @param featureCollection Bike station features.
     */
    private applyBikeStationsToMap( featureCollection: FeatureCollection ): void {
        const map = this.map;
        if ( map === null ) {
            return;
        }

        const source = map.getSource( BIKE_STATIONS_SOURCE_ID );

        if ( source !== undefined && "setData" in source ) {
            ( source as maplibregl.GeoJSONSource ).setData( featureCollection );
        }
    }

    /**
     * Clears the bike station source to an empty collection.
     */
    private clearBikeStationsOnMap(): void {
        this.applyBikeStationsToMap( EMPTY_BIKE_STATIONS_FEATURE_COLLECTION );
    }

    /**
     * Syncs map bike station source from the preload store (bbox-aware, sticky while loading).
     *
     * @param options Optional preload update flags.
     */
    async syncFromPreload( options: { "force"?: boolean } = {} ): Promise<void> {
        const map = this.map;
        if ( map === null || !this.isAttached || !this.isVisible ) {
            return;
        }

        const context = this.buildViewportContext( map );
        if ( context === null ) {
            return;
        }

        const result = await this.preloadStore.updatePreloadIfNeeded( context, options );

        if ( result.status === "belowMinZoom" ) {
            this.clearBikeStationsOnMap();

            return;
        }

        if ( result.status === "fetched" ) {
            const stations = this.preloadStore.getPreloadedData();
            if ( stations === null ) {
                return;
            }

            const featureCollection = bikeStationsToFeatureCollection( stations );
            this.applyBikeStationsToMap( featureCollection );

            if ( import.meta.env.DEV ) {
                console.debug(
                    "[BikeStationMapLayersController]",
                    "syncFromPreload",
                    result.status,
                    {
                        "bikeStationsFetched": result.itemsFetched,
                        "fetchedBbox":         result.fetchedBbox,
                    },
                );
            }

            return;
        }

        if ( result.status === "failed" && this.preloadStore.getPreloadedData() === null ) {
            this.clearBikeStationsOnMap();
        }
    }

}
